import { ArrowRight, BarChart3, Check, Clock, DollarSign, Layers, Package, Sparkles, Store, Target, TrendingUp, Zap } from "lucide-react";
import { useAppDispatch } from "../../../../app/hooks";
import useSimulationReadiness, {
    type SimulationSetupField,
    type SimulationSetupFieldKey
} from "../../../../hooks/useSimulationReadiness";
import { requestGenerateSimulation } from "../../../../slices/chatSlice";
import ActionButton from "../../components/atoms/actionButton";
import MetaCard from "../../components/atoms/metaCard";
import ProgressBar from "../../components/atoms/progressBar";
import StateIndicator from "../../components/atoms/stateIndicator";

const FIELD_ICON_SIZE = 16;
const fieldIcons: Record<SimulationSetupFieldKey, React.ReactNode> = {
    retailer: <Store size={FIELD_ICON_SIZE} />,
    planningLevel: <Layers size={FIELD_ICON_SIZE} />,
    selection: <Package size={FIELD_ICON_SIZE} />,
    timeframe: <Clock size={FIELD_ICON_SIZE} />,
    goal: <Target size={FIELD_ICON_SIZE} />,
    budget: <DollarSign size={FIELD_ICON_SIZE} />
};

const outcomePlaceholders = [
    {
        title: "Revenue Impact",
        description: "Estimated revenue outcome from the recommended plan.",
        icon: <TrendingUp size={18} />
    },
    {
        title: "Profit Impact",
        description: "Expected profit contribution after trade investment.",
        icon: <DollarSign size={18} />
    },
    {
        title: "Volume Impact",
        description: "Projected incremental unit sales over the horizon.",
        icon: <BarChart3 size={18} />
    },
    {
        title: "Investment Efficiency",
        description: "Expected return on commercial investment.",
        icon: <Zap size={18} />
    }
];

const SimulationOutcomesPreview = () => (
    <div className="border rounded-3 p-3 p-md-4 d-flex flex-column gap-2">
        <div>
            <h3 className="fs-7 fw-semibold mb-1">Simulation Outcomes</h3>
            <p className="fs-8 k-text-subtle mb-0">
                Complete your simulation setup to preview projected commercial outcomes.
            </p>
        </div>
        <div className="row g-2 opacity-50">
            {outcomePlaceholders.map(outcome => (
                <div key={outcome.title} className="col-12 col-md-6 col-xl-3">
                    <div className="d-flex flex-column border rounded-3 p-3 h-100 bg-white">
                        <span className="k-bg-subtle opacity-50 d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0  my-2" style={{ width: "32px", height: "32px" }}>
                            <span className="d-inline-flex flex-shrink-0">{outcome.icon}</span>
                        </span>
                        <span className="fs-8 fw-semibold text-body py-1">{outcome.title}</span>
                        <p className="fs-8 text-secondary mb-0">{outcome.description}</p>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const GuidanceChecklist = ({ fields }: { fields: SimulationSetupField[] }) => {
    const requiredFields = fields.filter(field => !field.isOptional);
    const completedFields = requiredFields.filter(field => field.isComplete);
    const remainingFields = requiredFields.filter(field => !field.isComplete);
    const budgetField = fields.find(field => field.key === "budget");

    if (completedFields.length === 0) {
        return (
            <ul className="mb-2 ps-3">
                {requiredFields.map(field => (
                    <li key={field.key} className="mb-1">{field.guidanceLabel}</li>
                ))}
                {budgetField && (
                    <li className="mb-0 text-secondary">
                        Optional: Budget (or let Sovra recommend)
                    </li>
                )}
            </ul>
        );
    }

    return (
        <div className="d-flex flex-column gap-2 fs-8">
            <ul className="list-unstyled mb-0">
                {completedFields.map(field => (
                    <li key={field.key} className="d-flex align-items-start gap-2 mb-1">
                        <Check size={14} strokeWidth={3} className="k-text-brand-teal flex-shrink-0 mt-1" />
                        <span>
                            {field.guidanceLabel}
                            {field.key === "retailer" ? " selected" : ""}
                            {": "}
                            <span className="fw-semibold">{field.value}</span>
                        </span>
                    </li>
                ))}
            </ul>
            {remainingFields.length > 0 && (
                <>
                    <p className="mb-0">
                        To generate your simulation, I still need:
                    </p>
                    <ul className="ps-3 mb-2">
                        {remainingFields.map(field => <li key={field.key} className="mb-1">{field.guidanceLabel}</li>)}
                    </ul>
                </>
            )}
            {budgetField && !budgetField.isComplete && (
                <p className="text-secondary mb-0">
                    Optional: Budget (or let Sovra recommend)
                </p>
            )}
        </div>
    );
};

const SimulationReadiness: React.FC = () => {
    const dispatch = useAppDispatch();
    const {
        fields,
        completedCount,
        requiredCount,
        nextAction,
        isReady,
        canGenerate
    } = useSimulationReadiness();

    const handleGenerateClick = (): void => {
        if (!canGenerate) {
            return;
        }

        dispatch(requestGenerateSimulation());
    };

    return (
        <div className="d-flex flex-column gap-4 min-w-0">
            <div className="d-flex flex-column gap-3">
                <div>
                    <p className="fs-8 text-uppercase fw-semibold !k-text-subtle mb-1 letter-spacing-wide">
                        Simulator
                    </p>
                    <h1 className="h4 fw-semibold simulator-title k-text-highlight-primary mb-2">
                        Let's Build Your Commercial Plan
                    </h1>
                    <p className="fs-7 text-secondary mb-0">
                        Configure your scenario and Sovra will generate the optimal commercial plan.
                    </p>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <ProgressBar totalCount={requiredCount} completedCount={completedCount} />
                </div>
            </div>

            <div className="simulator-plan-grid simulator-plan-grid--scenario-primary">
                {fields.map(field => (
                    <div key={field.key} className="d-flex min-w-0">
                        <MetaCard
                            icon={fieldIcons[field.key]}
                            label={field.label}
                            value={field.key === "budget" && isReady && !field.value
                                ? "AI Recommended"
                                : field.value ?? undefined}
                            placeholder={field.placeholder}
                            variant="highlighted"
                        />
                    </div>
                ))}
            </div>

            <SimulationOutcomesPreview />

            <div className="border rounded-3 p-3 p-md-4 bg-white d-flex flex-column gap-3">
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <h3 className="fs-7 fw-semibold mb-0">Simulation Readiness</h3>
                    <StateIndicator
                        isSuccessful={isReady}
                        successText="Ready to Run"
                        warningText="Missing Inputs"
                    />
                </div>

                <div className="d-flex flex-column gap-2">
                    <ProgressBar totalCount={requiredCount} completedCount={completedCount} showCountProgress={false} />
                </div>

                <div className="border rounded-3 p-3 k-bg-brand-light-teal !k-border-brand-ai-surface">
                    <div className="d-flex align-items-center gap-2 mb-2 k-text-brand-teal">
                        <span className="k-bg-brand-light-green d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0  my-2" style={{ width: "32px", height: "32px" }}>
                            <Sparkles size={14} strokeWidth={2} />
                        </span>
                        <span className="fs-8 fw-semibold text-uppercase letter-spacing-wide mb-0">
                            Sovra AI Guidance
                        </span>
                    </div>
                    <GuidanceChecklist fields={fields} />
                    <div>
                        <p className="fs-8 fw-medium mb-0 border-top pt-1">
                            Next:
                            {" "}
                            <span className="k-text-brand-teal">{nextAction}</span>
                        </p>
                    </div>
                </div>
            </div>

            <div className="d-flex flex-column gap-2">
                <ActionButton
                    variant="primary"
                    innerClassName="w-100 d-inline-flex align-items-center justify-content-center gap-2 py-2"
                    disabled={!canGenerate}
                    startIcon={<Sparkles size={16} strokeWidth={2} />}
                    endIcon={<ArrowRight size={16} strokeWidth={2} />}
                    onClick={handleGenerateClick}
                >
                    Generate Simulation
                </ActionButton>
                <p className="small text-secondary text-center mb-0">
                    {canGenerate
                        ? "Sovra will generate recommended pricing, promotion and retail media investments to maximise your objective."
                        : "Complete all required parameters to generate your simulation."}
                </p>
            </div>
        </div>
    );
};

export default SimulationReadiness;
